import { useEffect } from "react";

/**
 * Global body-scroll lock.
 *
 * Every popup in the app renders a full-screen overlay (`fixed inset-0` with a
 * high z-index). Without locking, scrolling *inside* an open popup — or over its
 * backdrop — bleeds through and scrolls the page behind it. This one manager
 * watches the DOM for any such overlay and, while at least one is present,
 * freezes the page body (compensating for the scrollbar width so the layout
 * doesn't shift). Mounted once at the app root, it covers every current and
 * future modal without each one having to opt in.
 */
export default function ScrollLockManager() {
  useEffect(() => {
    const body = document.body;
    let locked = false;

    // An "overlay" is a fixed, full-screen element sitting above the page
    // (z-index >= 40). That matches every modal/popup backdrop in the app.
    const isOverlay = (el) => {
      if (!(el instanceof HTMLElement)) return false;
      const cls = el.className;
      if (typeof cls !== "string") return false;
      if (!cls.includes("fixed") || !cls.includes("inset-0")) return false;
      const cs = window.getComputedStyle(el);
      // Skip hidden / inactive overlays. NOTE: fixed elements always report
      // offsetParent === null, so we must NOT use it as a visibility test.
      //
      // `pointer-events: none` is the key signal for a *closing* overlay: our
      // opacity-fade drawers/menus flip that class instantly on close, while the
      // opacity animates down over ~300ms. Reading opacity alone would still see
      // ~1 mid-transition and keep the page locked (and no further DOM mutation
      // fires when the fade finishes, so it would never re-check) — which left the
      // page unscrollable until the next popup. pointer-events flips with no
      // transition, so it lets us detect the close on the very same tick.
      if (
        cs.display === "none" ||
        cs.visibility === "hidden" ||
        cs.pointerEvents === "none" ||
        parseFloat(cs.opacity || "1") === 0
      ) {
        return false;
      }
      const z = parseInt(cs.zIndex, 10);
      return Number.isFinite(z) && z >= 40;
    };

    const anyOverlayOpen = () => {
      const nodes = document.querySelectorAll('[class*="fixed"][class*="inset-0"]');
      for (const n of nodes) if (isOverlay(n)) return true;
      return false;
    };

    const apply = () => {
      const shouldLock = anyOverlayOpen();
      if (shouldLock === locked) return;
      locked = shouldLock;
      if (shouldLock) {
        const scrollbar = window.innerWidth - document.documentElement.clientWidth;
        body.dataset.prevOverflow = body.style.overflow;
        body.dataset.prevPadRight = body.style.paddingRight;
        body.style.overflow = "hidden";
        if (scrollbar > 0) body.style.paddingRight = `${scrollbar}px`;
      } else {
        body.style.overflow = body.dataset.prevOverflow || "";
        body.style.paddingRight = body.dataset.prevPadRight || "";
        delete body.dataset.prevOverflow;
        delete body.dataset.prevPadRight;
      }
    };

    // Re-evaluate on any DOM change, coalesced to one check per frame.
    let raf = 0;
    const schedule = () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        apply();
      });
    };

    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "style"],
    });
    // Safety net for opacity-fade overlays: a class change fires one mutation at
    // the START of the fade (opacity still ~1), but nothing fires when it finishes
    // at opacity 0. Re-check on transitionend so the lock always clears once a fade
    // completes, even for overlays that don't toggle pointer-events.
    const onTransitionEnd = (e) => {
      if (e.propertyName === "opacity" || e.propertyName === "visibility" || e.propertyName === "transform") schedule();
    };
    document.addEventListener("transitionend", onTransitionEnd, true);
    apply();

    return () => {
      observer.disconnect();
      document.removeEventListener("transitionend", onTransitionEnd, true);
      if (raf) window.cancelAnimationFrame(raf);
      // Never leave the page frozen if this unmounts mid-lock.
      body.style.overflow = body.dataset.prevOverflow || "";
      body.style.paddingRight = body.dataset.prevPadRight || "";
      delete body.dataset.prevOverflow;
      delete body.dataset.prevPadRight;
    };
  }, []);

  return null;
}
